/**
 * Per-tab DUST session identifier.
 *
 * Operator direct mode binds each tab to its own session-targeted MITE so that a
 * single stalled connection cannot silence streaming in every other tab. A
 * collision between two tabs on the same identifier produces a DUST
 * `409 MITE_CONFLICT`, so the identifier must be distinct per browser tab.
 *
 * A plain in-memory (module-scoped) id satisfies this naturally:
 *   - Each tab runs its own JS context, so the id is unique per tab. Duplicating
 *     or opening a tab does a fresh page load (it does NOT copy the JS heap), so
 *     the clone generates its own id rather than inheriting one.
 *   - bfcache (back/forward) restores the JS heap, so the id is preserved and the
 *     tab reconnects to the same MITE without churning a new lease.
 *   - A full reload tears down the heap and mints a new id (new MITE). That is
 *     harmless: the prior SSE connection is closed on pagehide (its lease is
 *     released) and Operator does not restore the previous chat on reload anyway.
 *
 * Deliberately NOT persisted to sessionStorage: browsers copy sessionStorage into
 * duplicated/opened-from tabs, which would hand two live tabs the same id and
 * cause the exact MITE conflict this identifier exists to avoid.
 *
 * The id is sent to /dust/auth as `tabIdentifier` (salted into the derived
 * session id by DustController#derive_dust_session_id) and on the Operator
 * request as `dustSessionId`, so the streamer publishes to the same tab-specific
 * MITE the tab subscribed to.
 */

import { uuidv4 } from "@lib/uuidHelper";

// Module-scoped, so it is stable for the life of this tab's JS context (incl.
// bfcache restore) and unique per tab.
let cachedTabSessionId: string | null = null;

const generateId = (): string => {
  // crypto.randomUUID is preferred. Falls back to the uuid library for
  // environments that lack it (older browsers, non-secure contexts).
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return uuidv4();
};

/**
 * Returns this tab's DUST session identifier, generating one on first use and
 * reusing it for the life of the tab's JS context.
 */
export const getDustTabSessionId = (): string => {
  if (!cachedTabSessionId) {
    cachedTabSessionId = generateId();
  }
  return cachedTabSessionId;
};

/**
 * Clears the in-memory id. Intended for tests so each case starts clean.
 */
export const resetDustTabSessionIdCache = (): void => {
  cachedTabSessionId = null;
};
